import { useEffect, useState, useCallback } from 'react';
import type { ProfessionalOnlineStatus, AvailabilityUpdatePayload, AvailabilitySnapshotPayload } from '@klaro7/shared-types';

import { useSharedSocket } from '@/features/calls/context/CallContext';

type AvailabilityMap = Map<string, ProfessionalOnlineStatus>;

export function useAvailability() {
  // Shared CallProvider socket: this hook lives inside ConsultantCard /
  // FeaturedCard, so one socket per instance meant one connection per rendered
  // card (a full listing opened dozens of them). Handlers are per-instance,
  // the connection is not.
  const socket = useSharedSocket();
  const [availabilityMap, setAvailabilityMap] = useState<AvailabilityMap>(new Map());
  // True once the live snapshot has arrived: from that point the map is the
  // authority and a missing entry means offline. Before (or without) a socket
  // — anonymous visitors have no token, hence no shared socket — getStatus
  // falls back to the REST value.
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    if (!socket) {
      setIsLive(false);
      setAvailabilityMap((prev) => (prev.size > 0 ? new Map() : prev));
      return;
    }

    const handleConnect = () => {
      socket.emit('availability:subscribe');
    };

    const handleSnapshot = (snapshot: AvailabilitySnapshotPayload[]) => {
      setIsLive(true);
      setAvailabilityMap((prev) => {
        const next = new Map(prev);
        for (const item of snapshot) {
          next.set(item.professionalId, item.onlineStatus);
        }
        return next;
      });
    };

    const handleUpdate = (update: AvailabilityUpdatePayload) => {
      setAvailabilityMap((prev) => {
        const next = new Map(prev);
        if (update.onlineStatus === 'offline') {
          next.delete(update.professionalId);
        } else {
          next.set(update.professionalId, update.onlineStatus);
        }
        return next;
      });
    };

    socket.on('connect', handleConnect);
    socket.on('availability:snapshot', handleSnapshot);
    socket.on('availability:update', handleUpdate);

    // The shared socket is normally already connected when a card mounts, so
    // 'connect' would never fire again: subscribe immediately. The server-side
    // handler is idempotent (room join + snapshot), so re-subscribing costs a
    // snapshot and nothing else.
    if (socket.connected) socket.emit('availability:subscribe');

    return () => {
      // Remove ONLY our own handlers: the other cards share this socket and
      // must keep receiving updates after this instance unmounts. The socket
      // itself belongs to the CallProvider and is never disconnected here.
      socket.off('connect', handleConnect);
      socket.off('availability:snapshot', handleSnapshot);
      socket.off('availability:update', handleUpdate);
    };
  }, [socket]);

  const getStatus = useCallback(
    (professionalId: string, fallback: ProfessionalOnlineStatus = 'offline'): ProfessionalOnlineStatus => {
      const live = availabilityMap.get(professionalId);
      if (live) return live;
      return isLive ? 'offline' : fallback;
    },
    [availabilityMap, isLive]
  );

  const setMyStatus = useCallback((status: ProfessionalOnlineStatus) => {
    socket?.emit('availability:set', { status });
  }, [socket]);

  return { getStatus, setMyStatus, availabilityMap };
}
